import * as React from "react";
import { useState, useEffect, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Divider, Modal } from "semantic-ui-react";
import * as _ from "lodash";
import { nameofFactory, hasWriteAccess, getNotificationObject } from "../../utils/utils";
import { warning } from "react-notification-system-redux";
import Permissions from "../../components/Permissions/Permissions";
import {
  getFaultPriorityOptionsByDomainId,
  getDomainOptionsByDomainId,
  getGroupNameByDomainId,
  getKceVersionOptions,
  getDataActOptions,
  getFaultCategoryOptions,
  DropdownOption
} from "../../utils/pdApiUtils";
import Editor from "../../components/Editing/Editor";
import TextField from "../../components/Editing/TextField";
import CheckboxField from "../../components/Editing/CheckboxField";
import NumberField from "../../components/Editing/NumberField";
import DropdownField from "../../components/Editing/DropdownField";
import { editActions } from "../../actions/editActions";
import ButtonField from "../../components/Editing/ButtonField";
import TextIndicatorField from "../../components/Editing/TextIndicatorField";
import { EditLocalizationStatus } from "./EditLocalizationStatus";

const faultFields = nameofFactory<Api.BaseFault>();
const apmFaultFields = nameofFactory<Api.APMFault>();
const signalizationFaultFields = nameofFactory<Api.SignalizationFault>();
const networkFaultFields = nameofFactory<Api.NetworkFault>();
const monitoringFaultFields = nameofFactory<Api.MonitoringFault>();
const None = -1;

// eslint-disable-next-line
const EditFaultCommon: React.FC<{ toggleModal: () => void; columns: any }> = ({
  toggleModal,
  columns
}) => {
  const dispatch = useDispatch();
  const editedFault = useSelector((state: State.Root) => state.editView.editedObject);
  const storedFault = useSelector((state: State.Root) => state.editView.storedObject);
  const pdApi = useSelector((state: State.Root) => state.pdApi);
  const activeUser = useSelector((state: State.Root) => state.main.activeUser);
  const isShortenTextEnabled = useSelector((state: State.Root) => state.main.isShortenTextEnabled);

  const [isEditLocalizationStatusOpen, setIsEditLocalizationStatusOpen] = useState(false);
  const [locsOriginal, setLocsOriginal] = useState<Api.LocsFault[]>([]);
  const [locsEdited, setLocsEditedState] = useState<Api.LocsFault[]>([]);
  const [userPermissions, setUserPermissions] = useState(
    editedFault?.domain?.id
      ? hasWriteAccess(activeUser, getGroupNameByDomainId(editedFault.domain.id, pdApi))
      : false
  );

  useEffect(() => {
    if (editedFault?.domain?.id) {
      setUserPermissions(
        hasWriteAccess(activeUser, getGroupNameByDomainId(editedFault.domain.id, pdApi))
      );
    }
  }, [activeUser, editedFault?.domain?.id, pdApi]);

  // Check if domain is missing or domain options are empty
  useEffect(() => {
    if (editedFault && (!editedFault.domain || (editedFault.domain?.id && getDomainOptionsByDomainId(editedFault.domain.id, false, pdApi).filter(d => d.text !== "KCELIO").length === 0))) {
      dispatch(warning(getNotificationObject("warning", "Domains must be created to add a fault.", { autoDismiss: 10 })));
      dispatch(editActions.cancelEditing());
    }
  }, [editedFault, pdApi, dispatch]);

  // Early return if editedFault or its domain is not defined
  if (!editedFault || !editedFault.domain) {
    return null;
  }

  const handleEditedFaultChanged = useCallback(
    <T,>(name: string, value: T) => {
      dispatch(editActions.setEdited({ ...editedFault, [name]: value }));
    },
    [dispatch, editedFault]
  );

  const englishTranslationsChanged = useCallback(
    () =>
      editedFault.englishName !== storedFault?.englishName ||
      editedFault.action !== storedFault?.action ||
      editedFault.reason !== storedFault?.reason,
    [editedFault, storedFault]
  );

  const toggleEditLocalizationStatusModal = useCallback(() => {
    setIsEditLocalizationStatusOpen(prevState => !prevState);
  }, []);

  const setLocalizationsEdited = useCallback(
    (newLocalizations: Api.LocsFault[], oldLocalizations: Api.LocsFault[]) => {
      setLocsEditedState(newLocalizations);
      setLocsOriginal(oldLocalizations);
      dispatch(
        editActions.setEdited({
          ...editedFault,
          englishName:
            newLocalizations.find(loc => loc.localeNavigation.locale === "en-US")?.text ||
            editedFault?.englishName,
          reason:
            newLocalizations.find(loc => loc.localeNavigation.locale === "en-US")?.reason ||
            editedFault?.reason,
          action:
            newLocalizations.find(loc => loc.localeNavigation.locale === "en-US")?.action ||
            editedFault?.action,
          locs: newLocalizations
        })
      );
    },
    [dispatch, editedFault]
  );

  const setLocalizationsNonEdited = useCallback((Localizations: Api.LocsFault[]) => {
    setLocsOriginal(Localizations);
  }, []);

  const isEmptyString = (str: string): boolean => !(!!str && str.replace(/\s/g, "").length > 0);

  const isFieldValid = (field: string): boolean => {
    switch (field) {
      case faultFields("code"):
        return isCodeValid();
      case faultFields("name"):
        return isNameValid();
      case faultFields("englishName"):
        return !isEmptyString(editedFault.englishName);
      case faultFields("reason"):
        return !isEmptyString(editedFault.reason);
      case faultFields("action"):
        return !isEmptyString(editedFault.action);
      case apmFaultFields("codeVariant"):
        return (
          !isEmptyString((editedFault as Api.APMFault).codeVariant) &&
          (editedFault as Api.APMFault).codeVariant.length <= 10
        );
      case apmFaultFields("enumName"):
        return isEnumNameValid();
      case signalizationFaultFields("enumName"):
        return isEnumNameValid();
      case networkFaultFields("enumName"):
        return isEnumNameValid();
      case signalizationFaultFields("releaseStatus"): {
        return !isEmptyString((editedFault as Api.SignalizationFault).releaseStatus);
      }
      case monitoringFaultFields("faultCategory"): {
        return getFaultCategoryOptions(pdApi.monitoringFaultCategories || []).length > 0;
      }
      default:
        return true;
    }
  };

  const isAllFieldsValid = (): boolean => {
    const temp = _.keys(editedFault);
    for (const key of temp) {
      if (!isFieldValid(key)) {
        return false;
      }
    }
    return true;
  };

  const isCodeValid = (): boolean => {
    const isNew = !storedFault;

    // For other fault types, check uniqueness within the domain
    return parseInt(editedFault.code) <= 0 ||
      _.values(pdApi.faults).find(
        (fault: Api.AnyFault) =>
          (isNew || fault.id !== editedFault.id) &&
          fault.domain.id === editedFault.domain.id &&
          fault.code === editedFault.code.toString()
      )
      ? false
      : true;
  };

  const isNameValid = (): boolean => {
    // Check that name is not empty
    if (isEmptyString(editedFault.name)) {
      return false;
    }

    return true;
  };

  const isEnumNameValid = (): boolean => {
    const { domains, domainGroups } = pdApi;
    const isNew = !storedFault;
    const faultDomain = getGroupNameByDomainId(editedFault.domain.id, pdApi);
    const editedFaultTemp = editedFault as
      | Api.LiftFault
      | Api.APMFault
      | Api.SignalizationFault
      | Api.NetworkFault;
    const ENUM_SUPPORTED_DOMAINS = ["APM", "Signalization", "Network", "Monitoring"]; // Define fault domains that support enumName validation
    const enumFaults = ENUM_SUPPORTED_DOMAINS.includes(faultDomain)
      ? pdApi.faults.filter((fault: Api.AnyFault) => {
          const groupId = domains[fault.domain.id]?.domainGroup.id;
          const groupName = domainGroups[groupId]?.enumName;
          return groupName === faultDomain;
        })
      : [];
    const isNotEmpty = !isEmptyString(editedFaultTemp.enumName);
    const isUnique =
      _.values(enumFaults).find(fault => {
        if (
          (isNew || fault.id !== editedFaultTemp.id) &&
          (fault as
            | Api.LiftFault
            | Api.APMFault
            | Api.SignalizationFault
            | Api.NetworkFault).enumName
            ?.trim()
            .toLowerCase() === editedFaultTemp.enumName?.trim().toLowerCase()
        ) {
          return fault;
        }
      }) === undefined;

    return isNotEmpty && isUnique;
  };

  const textField = (
    label: string,
    field: string,
    readonly?: boolean,
    disabled?: boolean,
    isSingleLine?: boolean,
    errorMessage?: string
  ): JSX.Element => {
    const value = editedFault[field];
    return (
      <TextField
        id={label.replace(/\s/g, "")}
        label={label}
        disabled={disabled}
        readonly={readonly}
        value={value}
        isError={!isFieldValid(field)}
        isSingleLine={isSingleLine}
        onChange={changedValue =>
          dispatch(editActions.setEdited({ ...editedFault, [field]: changedValue.trim() }))
        }
        errorMessage={errorMessage}
      />
    );
  };

  const checkboxField = (label: string, field: string, disabled?: boolean): JSX.Element => {
    const checked = editedFault[field];
    return (
      <CheckboxField
        id={label.replace(/\s/g, "")}
        label={label}
        checked={checked}
        onChange={value => dispatch(editActions.setEdited({ ...editedFault, [field]: value }))}
        disabled={disabled}
      />
    );
  };

  const numberFieldAsString = (
    label: string,
    field: string,
    readOnly?: boolean,
    disabled?: boolean,
    errorMessage?: string
  ): JSX.Element => {
    editedFault["code"] = editedFault["code"] === "" ? "0" : editedFault["code"];
    const numberValue: number = parseInt(editedFault["code"]);
    return (
      <NumberField
        id={label.replace(/\s/g, "")}
        label={label}
        disabled={disabled}
        readonly={readOnly}
        value={numberValue}
        isError={!isFieldValid(field)}
        onChange={value =>
          dispatch(editActions.setEdited({ ...editedFault, [field]: value.toString() }))
        }
        errorMessage={errorMessage}
      />
    );
  };

  const commonProperties = (isNew: boolean, groupName: Api.GroupName): JSX.Element => {
    const domainId = editedFault.domain.id;
    const priorityOptions = getFaultPriorityOptionsByDomainId(domainId, pdApi);
    let domainOptions = getDomainOptionsByDomainId(domainId, false, pdApi);
    domainOptions = domainOptions.filter(domain => domain.text !== "KCELIO");

    return (
      <>
        {numberFieldAsString(
          "Code",
          faultFields("code"),
          !isNew,
          false,
          groupName === "Escalator" || groupName === "Group"
            ? "The code must be unique in the domain."
            : groupName === "Monitoring"
            ? "The code must be greater than zero and unique across all monitoring faults."
            : "The code must be greater than zero and unique in the domain."
        )}
        {isNew ? (
          <DropdownField
            id="domainName"
            label="Domain name"
            value={editedFault.domain.id}
            options={domainOptions}
            search={true}
            onChange={id => {
              const domain = pdApi.domains[id];
              dispatch(editActions.setEdited({ ...editedFault, domain }));
            }}
          />
        ) : (
          <TextField
            id="domainNameText"
            label="Domain name"
            readonly={true}
            value={
              pdApi.domains[domainId].enumName + " [" + pdApi.domains[domainId].enumValue + "]"
            }
            isError={false}
            onChange={() => undefined}
          />
        )}
        <br />
        <DropdownField
          id="priority"
          label="Priority"
          value={editedFault.priority?.id || ""}
          options={priorityOptions}
          search={true}
          onChange={id => {
            const priority = {
              id: pdApi.faultPriorities[id].id,
              url: pdApi.faultPriorities[id].url
            };
            dispatch(editActions.setEdited({ ...editedFault, priority }));
          }}
          disabled={!userPermissions}
        />
        <br />
        {textField(
          "Name",
          faultFields("name"),
          !userPermissions
        )}

        {groupName !== "NonKone" && (
          <>
            {textField("Comment", faultFields("comment"), !userPermissions)}
            {textField("Detection", faultFields("detection"), !userPermissions)}
            {textField("Recovery", faultFields("recovery"), !userPermissions)}
            {textField("Operation", faultFields("operation"), !userPermissions)}
            <Permissions
              permissions={editedFault.permissions}
              userGroups={pdApi.userGroups}
              onChanged={permissions => {
                handleEditedFaultChanged(faultFields("permissions"), permissions);
              }}
              disabled={!userPermissions}
            />
          </>
        )}

        <Divider horizontal={true}>{"English translations"}</Divider>
        <TextIndicatorField
          id={"needsUpdateIndicator"}
          text={"Edited texts will be translated."}
          isWarning={englishTranslationsChanged()}
        />
        {textField("English name", faultFields("englishName"), !userPermissions)}
        {textField("Reason", faultFields("reason"), !userPermissions)}
        {textField("Action", faultFields("action"), !userPermissions)}
        <ButtonField
          id={"viewTranslationsButton"}
          label="View translations"
          onButtonClick={toggleEditLocalizationStatusModal}
        />
      </>
    );
  };

  const specificProperties = (groupName: Api.GroupName): JSX.Element => {
    if (groupName === "APM") {
      return apmProperties();
    } else if (groupName === "Signalization") {
      return signalizationProperties();
    } else if (groupName === "Network") {
      return networkProperties();
    } else if (groupName === "Monitoring") {
      return monitoringProperties();
    } else {
      return apmProperties();
    }
  };

  // Display APM properties
  const apmProperties = (): JSX.Element => {
    const apmFault = editedFault as Api.APMFault;
    const kceVersionOptions = getKceVersionOptions(pdApi.kceVersions);
    const valueFromVersion = apmFault.validFromVersion ? apmFault.validFromVersion.id : None;
    const valueUntilVersion = apmFault.validUntilVersion ? apmFault.validUntilVersion.id : None;
    const dataActOptions = getDataActOptions(pdApi.dataActCodes || []);

    return (
      <>
        {textField(
          "Enumerations",
          apmFaultFields("enumName"),
          !userPermissions,
          false,
          false,
          !isEmptyString(apmFault.enumName)
            ? "The enumerations must be unique in the domain group."
            : ""
        )}
        <DropdownField
          id="dataAct"
          label="DataAct Category"
          value={apmFault.dataActId}
          options={dataActOptions}
          onChange={id => {
            dispatch(editActions.setEdited({ ...editedFault, dataActId: id }));
          }}
          disabled={!userPermissions}
          search={true}
        />
        <DropdownField
          id="validFrom"
          label="Valid from version"
          value={valueFromVersion}
          options={kceVersionOptions}
          onChange={id => {
            const validFromVersion =
              id === None ? null : { id: pdApi.kceVersions[id].id, url: null };
            dispatch(editActions.setEdited({ ...editedFault, validFromVersion }));
          }}
          disabled={!userPermissions}
        />
        <DropdownField
          id="validUntil"
          label="Valid until version"
          value={valueUntilVersion}
          options={kceVersionOptions}
          onChange={id => {
            const validUntilVersion =
              id === None ? null : { id: pdApi.kceVersions[id].id, url: null };
            dispatch(editActions.setEdited({ ...editedFault, validUntilVersion }));
          }}
          disabled={!userPermissions}
        />

        {textField(
          "Code variant",
          apmFaultFields("codeVariant"),
          !userPermissions,
          false,
          false,
          !isEmptyString(apmFault.codeVariant)
            ? "The Maximum number of characters must not exceed 10."
            : ""
        )}
        {textField("Additional info def.", apmFaultFields("additionalInfoDef"), !userPermissions)}
        <div className="spaced-flex-row">
          {checkboxField("Setup req.", apmFaultFields("setupReq"), !userPermissions)}
          {checkboxField("Manual reset", apmFaultFields("manualReset"), !userPermissions)}
          {checkboxField("Start prevented", apmFaultFields("startPrevented"), !userPermissions)}
          {checkboxField("SC Open", apmFaultFields("scOpen"), !userPermissions)}
          {checkboxField("Change board", apmFaultFields("changeBoard"), !userPermissions)}
        </div>
      </>
    );
  };

  // Display Signalization properties
  const signalizationProperties = (): JSX.Element => {
    const signalizationFault = editedFault as Api.SignalizationFault;
    return (
      <>
        {textField(
          "Enumerations",
          signalizationFaultFields("enumName"),
          !userPermissions,
          false,
          false,
          !isEmptyString(signalizationFault.enumName)
            ? "The enumerations must be unique in the domain group."
            : ""
        )}
        {textField("Release Status", signalizationFaultFields("releaseStatus"), !userPermissions)}
      </>
    );
  };

  const networkProperties = (): JSX.Element => {
    const networkFault = editedFault as Api.NetworkFault;
    return (
      <>
        {textField(
          "Enumerations",
          networkFaultFields("enumName"),
          !userPermissions,
          false,
          false,
          !isEmptyString(networkFault.enumName)
            ? "The enumerations must be unique in the domain group."
            : ""
        )}
        {textField(
          "Network additional data 1",
          networkFaultFields("data1Description"),
          !userPermissions
        )}

        {textField(
          "Network additional data 2",
          networkFaultFields("data2Description"),
          !userPermissions
        )}
        {textField("Testing", networkFaultFields("testing"), !userPermissions)}
      </>
    );
  };

  const monitoringProperties = (): JSX.Element => {
    const monitoringFault = editedFault as Api.MonitoringFault;
    const faultCategoryOptions: DropdownOption[] = pdApi.monitoringFaultCategories
      ? getFaultCategoryOptions(pdApi.monitoringFaultCategories)
      : [];
    const isNew = !storedFault;

    if (isNew && monitoringFault.faultCategory.url === "" && monitoringFault.id === undefined) {
      monitoringFault.faultCategory = { id: faultCategoryOptions[0]?.value as number, url: null };
    }

    return (
      <>
        <DropdownField
          id="category"
          label="Category"
          value={monitoringFault.faultCategory.id}
          options={faultCategoryOptions}
          onChange={id => {
            const faultCategory = {
              id: id,
              url: null
            };
            dispatch(editActions.setEdited({ ...editedFault, faultCategory }));
          }}
          disabled={!userPermissions}
          isError={faultCategoryOptions.length === 0}
          errorMessage="A fault category must be created before adding faults"
        />
        {textField(
          "Monitoring additional data 1",
          monitoringFaultFields("data1Description"),
          !userPermissions
        )}

        {textField(
          "Monitoring additional data 2",
          monitoringFaultFields("data2Description"),
          !userPermissions
        )}
        {textField(
          "Monitoring additional data 3",
          monitoringFaultFields("data3Description"),
          !userPermissions
        )}
        {textField(
          "Monitoring additional data 4",
          monitoringFaultFields("data4Description"),
          !userPermissions
        )}
        {checkboxField(
          "Fault is localizable",
          monitoringFaultFields("localizable"),
          !userPermissions
        )}
        {checkboxField(
          "Supervision can be adjusted with parameters",
          monitoringFaultFields("supervisionAdjustable"),
          !userPermissions
        )}
      </>
    );
  };

  const groupName = getGroupNameByDomainId(editedFault.domain.id, pdApi);
  const isNew = !storedFault;
  const storeOption: Api.OptionsGrud =
    groupName === "NonKone"
      ? "nonKoneFaults"
      : groupName === "Escalator"
      ? "escalatorFaults"
      : groupName === "Door"
      ? "doorFaults"
      : groupName === "APM"
      ? "apmFaults"
      : groupName === "Signalization"
      ? "signalizationFaults"
      : groupName === "Network"
      ? "networkFaults"
      : groupName === "Monitoring"
      ? "monitoringFaults"
      : "faults";

  if (storedFault) {
    storedFault.priority.url = pdApi.faultPriorities[storedFault.priority.id].url;
    if (storedFault.permissions.length > 0) {
      storedFault.permissions.forEach((permission: Api.Permission) => {
        if (permission.userGroup) {
          const index = _.findIndex(pdApi.userGroups, { id: permission.userGroup.id });
          permission.userGroup.url = pdApi.userGroups[index].url;
        }
      });
    }
  }

  return (
    <Editor
      itemName="fault"
      originalObject={storedFault}
      editedObject={editedFault}
      hasWritePermission={hasWriteAccess(activeUser, groupName)}
      isValid={isAllFieldsValid()}
      cancel={() => dispatch(editActions.cancelEditing())}
      update={() => {
        dispatch(editActions.setEdited({ ...editedFault, locs: locsEdited }));
        dispatch(editActions.update(storeOption));
      }}
      create={() => {
        dispatch(editActions.setEdited({ ...editedFault, locs: locsEdited }));
        dispatch(editActions.add(storeOption));
      }}
      copy={() => {
        dispatch(editActions.setEdited({ ...editedFault, locs: locsEdited }));
        dispatch(editActions.startEditing(editedFault, true));
      }}
      remove={{ callback: toggleModal }}
      copyAccess={groupName === "Group" ? false : true}
    >
      {commonProperties(isNew, groupName)}
      <Divider horizontal={true}>{`${groupName} properties`}</Divider>
      {specificProperties(groupName)}
      <Modal size="large" open={isEditLocalizationStatusOpen} className="data-sections-modal">
        <EditLocalizationStatus
          itemType={"Fault"}
          pdApi={pdApi}
          editedItem={{ ...editedFault, locs: locsEdited }}
          getOption={
            groupName === "NonKone"
              ? "nonkonefaultLocalizations"
              : groupName === "Escalator"
              ? "escalatorfaults"
              : groupName === "Door"
              ? "doorfaults"
              : "faultLocalizations"
          }
          columns={columns["Fault Localization Status"]}
          toggleEditLocalizationStatusModal={toggleEditLocalizationStatusModal}
          setLocalizationsEdited={setLocalizationsEdited}
          isShortenTextEnabled={isShortenTextEnabled}
          englishTranslationChanged={englishTranslationsChanged()}
          userPermissions={userPermissions}
          locsOriginal={locsOriginal || []}
          setLocalizationsNonEdited={setLocalizationsNonEdited}
        />
      </Modal>
    </Editor>
  );
};

export default EditFaultCommon;
